import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

/**
 * Mobile bottom sheet (Headless UI Dialog) with scrim, portal to body, z-[2100].
 * Hidden from lg breakpoint by default — matches project mobile nav pattern.
 */
export function MobileBottomSheet({
  open = false,
  onClose,
  title,
  ariaLabel,
  items = [],
  hideFromLg = true,
}) {
  const show = open && items.length > 0;

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className={`relative z-[2100] ${hideFromLg ? "lg:hidden" : ""}`}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 flex items-end justify-center overflow-y-auto p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <Dialog.Panel
              className="w-full max-w-lg transform rounded-t-2xl border-t border-gray-200 bg-white p-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-left shadow-xl transition-all max-h-[min(85vh,100%)] overflow-y-auto"
              role="menu"
              aria-label={ariaLabel || title}
            >
              {title ? (
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
                  {title}
                </div>
              ) : null}
              {items.map((item) => {
                const Icon = item.icon;
                const selected = Boolean(item.selected);
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    onClick={item.onClick}
                    className={`mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition active:bg-gray-100 ${
                      selected
                        ? "bg-teal-50 text-teal-800 hover:bg-teal-50"
                        : "text-gray-900 hover:bg-gray-50"
                    }`}
                    aria-current={selected ? "true" : undefined}
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${item.iconBackground || "bg-teal-50"} ${item.iconForeground || "text-teal-700"}`}
                    >
                      {Icon ? <Icon className="h-5 w-5" aria-hidden /> : null}
                    </span>
                    <span className="min-w-0">
                      <span className="block">{item.label}</span>
                      {item.description ? (
                        <span className="block text-xs font-normal text-gray-500">
                          {item.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default MobileBottomSheet;
